export type TOmanShippingMethodId = 'delivery' | 'pickup'

export interface TOmanShippingContact {
    fullname?: string
    email?: string
    phone?: string
}

export type TOmanPaymentMethod = 'creditcard' | 'creditCardAlias' | 'bankwire' | 'cashOnDelivery' | 'pickupPayment'

// Region (state) from Hydra /v5a/countries/{code}/regions.json
export interface TOmanRegion {
    id: string
    name: string
    code: string
    value: string
}

// Country list item from country_omn remote storage
export interface TOmanCountryItem {
    country_code_2: string
    name_english: string
    name_native: string
    flags_url: string
}

// Guest create-account + shipping form
export interface TOmanCheckoutForm {
    firstname: string
    lastname: string
    email: string
    phone: string
    password: string
    address1: string
    address2: string
    city: string
    state: string
    country: string
    terms: boolean
    pdpa: boolean
}

// Logged-in member shipping form (no password / agreements)
export interface TOmanShippingForm {
    fullname: string
    email: string
    phone: string
    country: string
    address1: string
    address2: string
    city: string
    state: string
}

// Credit card input form
export interface TOmanCreditCardForm {
    cardNumber: string
    expMonth: string
    expYear: string
    cvv: string
    saveCard: boolean
    billingSameAsShipping: boolean
    billing: {
        firstname: string
        country: string
        address1: string
        address2: string
        city: string
        state: string
    }
}

// Format data shape (subset used by Oman checkout)
export interface TOmanFormatData {
    uuid: string
    fullname?: string
    firstname?: string
    lastname?: string
    email?: string
    phone?: string
    address?: string
    address2?: string
    city?: string
    state?: string
    country?: string
    shipment_options?: string
    freight?: number
    subtotal?: number
    total?: number
    product_credit?: number
    tax?: { amount: number; percentage: number }
    items?: TOmanFormatItem[]
    hasSubscription?: boolean
}

export interface TOmanFormatItem {
    item_code: string
    name: string
    qty: number
    price: number
    pv?: number
    type?: 'subscription' | 'onetime'
}

// ─── Payment flow types ─────────────────────────────────────────────────────

export interface TOmanSaveCardAliasPayload {
    methodDetails: {
        alias: string
        creditCardNumber: string
        creditCardExpires: string
        payer: string
    }
    billToAddress: {
        country: string
        address1?: string
        address2?: string
        city?: string
        state?: string
        zip?: string
    }
    billToEmail: string
    billToPhone: string
    billToName: { firstName: string; lastName: string; fullName?: string }
}

// ─── PaymentsV3 types ───────────────────────────────────────────────────────

// Wire-format `method` values sent to `POST hot/payments/v3/.../variable`.
// Edit a single value here if the API renames a method (e.g. `cod` →
// `cashOnDelivery`) and every call site picks up the change.
export const PAYMENTS_V3_METHOD = {
    creditCard: 'creditCard',
    creditCardAlias: 'creditCardAlias',
    bankWire: 'bankWire',
    cod: 'cod',
    payOnPickup: 'payOnPickup'
} as const

export type PaymentsV3Method = (typeof PAYMENTS_V3_METHOD)[keyof typeof PAYMENTS_V3_METHOD]

export interface PaymentsV3BillToAddress {
    email: string
    phone: string
    fullname: string
    address: string
    address2?: string
    city: string
    state?: string
    country: string
}

export interface PaymentsV3PaymentItem {
    method: PaymentsV3Method
    amount?: number
    ccEncrypted?: string
    payer?: string
    aliasName?: string
    isBillAddressSameShipToAddress?: boolean
    billToAddress?: PaymentsV3BillToAddress
}

export interface PaymentsV3CreateRequest {
    uuid: string
    source_path: string
    payments: PaymentsV3PaymentItem[]
    gateway_success_url?: string
    gateway_error_url?: string
}

export type PaymentsV3NextAction = 'polling' | 'gateway_redirect' | 'redirect_url' | 'qr_display' | 'createOrderViaFE'

export interface PaymentsV3GatewayData {
    payment_action_url?: string
    redirect_url?: string
    qrUrl?: string
    expiryDescription?: string
    paymentRef?: string
    respDesc?: string
    amount?: string
    currency?: string
    [key: string]: string | undefined
}

export interface PaymentsV3CreateResponse {
    gateway_ref_id: string
    next_action: PaymentsV3NextAction
    poll_interval_ms?: number
    gateway_data?: PaymentsV3GatewayData
    formatHydra?: {
        hydra: Record<string, unknown>
        data: Record<string, unknown>
    }
    success?: boolean
    error?: { message: string }
}

export type PaymentsV3HydraStatus = '' | 'processing' | 'success' | 'fail' | 'timeout'
export type PaymentsV3PaymentStatus = 'waiting' | 'success' | 'fail'
export type StepStyle = { suffix: string; color: string; Icon: React.ComponentType }
export type ProcessingPaymentStepField = 'hydra_status' | 'payment_status'
export type ProcessingPaymentStepStatus = PaymentsV3HydraStatus | PaymentsV3PaymentStatus

export type ProcessingPaymentStep = {
    field: ProcessingPaymentStepField
    translationKey: string
    failures: ReadonlyArray<ProcessingPaymentStepStatus>
    configs: Record<string, StepStyle>
}

export interface PaymentsV3StatusResponse {
    uuid?: string
    gateway_ref_id: string
    payment_status: PaymentsV3PaymentStatus
    hydra_status: PaymentsV3HydraStatus
    order_number?: string
    autoship_id?: string
    hydra_response_body?: string
    gateway_data?: Record<string, unknown>
}

export interface PaymentsV3DuplicateOrderResponse {
    is_error_code: boolean
    is_duplicate: boolean
    is_waiting?: boolean
    payments_id?: number
    gateway_ref_id?: string
    date_time?: string
    total_price?: number
    currency?: string
    order_number?: string
    routing?: string
    message?: string
}

export type ProcessingPaymentStatus = 'idle' | 'submitting' | 'processing' | 'success' | 'error'

// Runtime state for the processing modal. Each step field stores only the
// raw API status value. Defaults live in `initialOmanProcessingPayment`.
export type ProcessingPaymentState = {
    status: ProcessingPaymentStatus
    hydra_status: PaymentsV3HydraStatus
    payment_status: PaymentsV3PaymentStatus
    hydra_response_body?: string
}

// ─── Credit card alias (local definition — avoids cross-app import from spotlight) ─
export interface TCreditcardAliasBillToAddress {
    address1: string
    address2: string
    city: string
    state: string
    zip: string
    country: string
}

export interface TCreditcardAliasMethodDetails {
    alias: string
    aliasIsAssociatedWithAutoOrder: boolean
    creditCardExpires: string
    creditCardNetwork: string
    creditCardNumberMasked: string
    payer: string
    creditCardNumber: string
}

export interface TOmanCreditcardAlias {
    methodDetails: TCreditcardAliasMethodDetails
    billToAddress: TCreditcardAliasBillToAddress
    billToEmail: string
    billToName: { firstName: string; lastName: string }
    billToPhone: string
    href: string
}

// createAccount POST body for Oman
export interface TOmanCreateAccountBody {
    type: 'enroll' | 'enrollWithReferral' | 'shop'
    uuid: string
    referral_id?: string
    firstname_en: string
    lastname_en: string
    email: string
    phone: string
    password: string
    address: string
    address2: string
    city: string
    state: string
    country: string
    shipment_options: string
    period: string
    shipment_packs: string
}
